import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import { ORG_TYPES } from "../lib/solutions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASE_FORM = {
  full_name: "",
  email: "",
  organisation: "",
  org_type: "School",
  role: "",
  requirements: "",
  phone: "",
  timeframe: "",
  budget: "",
  participant_count: "",
  company_website: "", // honeypot
};

/**
 * Reusable service-specific enquiry form.
 * Props:
 *  - service: slug string (e.g. "strategy") — becomes server-side service tag
 *  - serviceLabel: pretty name shown in headline
 *  - sourcePage: path or label recorded with the submission
 *  - accent: hex colour used on the submit button
 *  - ctaLabel: override the CTA button text (default: "Send enquiry")
 */
export default function ServiceEnquiryForm({
  service,
  serviceLabel,
  sourcePage,
  accent = "#0055FF",
  ctaLabel = "Send enquiry",
}) {
  const [form, setForm] = useState(BASE_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/service-enquiries`, {
        ...form,
        service,
        source_page: sourcePage,
      });
      setDone(true);
      toast.success("Thanks — we’ll be in touch within 1–2 business days.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`enquiry-success-${service}`}
        className="rounded-3xl border-2 border-[#1A1A1A] bg-white p-7 md:p-10 nb-shadow-lg"
      >
        <CheckCircle2 size={40} strokeWidth={2.5} className="text-[#00C2B8]" />
        <h3 className="mt-4 font-display font-black text-3xl md:text-4xl leading-tight tracking-tighter">
          Enquiry received.
        </h3>
        <p className="mt-3 text-[#4A4A4A] font-medium">
          Thanks, {form.full_name.split(" ")[0] || "there"}. Julija will reply
          to <strong>{form.email}</strong> within 1–2 business days with next
          steps for {serviceLabel}.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`enquiry-form-${service}`}
      className="rounded-3xl border-2 border-[#1A1A1A] bg-white p-6 md:p-8 nb-shadow-lg"
    >
      {/* Honeypot — hidden from real users; bots fill it */}
      <input
        type="text"
        name="company_website"
        value={form.company_website}
        onChange={upd("company_website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          width: 1,
          height: 1,
          opacity: 0,
        }}
      />

      <p className="font-mono text-[11px] uppercase tracking-widest font-bold text-[#FF007F]">
        Enquiry · {serviceLabel}
      </p>
      <h3 className="mt-1 font-display font-black text-2xl md:text-3xl tracking-tighter leading-tight">
        Tell us about your project.
      </h3>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Full name" required>
          <input required value={form.full_name} onChange={upd("full_name")} data-testid={`enquiry-fullname-${service}`} className="form-input" placeholder="Your full name" />
        </Field>
        <Field label="Email" required>
          <input required type="email" value={form.email} onChange={upd("email")} data-testid={`enquiry-email-${service}`} className="form-input" placeholder="you@org.edu" />
        </Field>
        <Field label="Organisation" required>
          <input required value={form.organisation} onChange={upd("organisation")} data-testid={`enquiry-org-${service}`} className="form-input" placeholder="School / Library / Council" />
        </Field>
        <Field label="Organisation type" required>
          <div className="flex flex-wrap gap-2">
            {ORG_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setForm({ ...form, org_type: t })}
                data-testid={`enquiry-orgtype-${service}-${t.toLowerCase()}`}
                className={`px-3.5 py-1.5 rounded-full border-2 text-xs font-bold uppercase tracking-wide transition-all ${
                  form.org_type === t
                    ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    : "bg-white text-[#1A1A1A] border-[#1A1A1A] nb-shadow-sm nb-press"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Role / job title" required>
          <input required value={form.role} onChange={upd("role")} data-testid={`enquiry-role-${service}`} className="form-input" placeholder="Principal, Librarian, Director…" />
        </Field>
        <Field label="Phone (optional)">
          <input value={form.phone} onChange={upd("phone")} data-testid={`enquiry-phone-${service}`} className="form-input" placeholder="04xx xxx xxx" />
        </Field>
        <Field label="Preferred timeframe (optional)">
          <input value={form.timeframe} onChange={upd("timeframe")} data-testid={`enquiry-timeframe-${service}`} className="form-input" placeholder="e.g. Term 2 · Semester 1 · ASAP" />
        </Field>
        <Field label="Approx. budget (optional)">
          <input value={form.budget} onChange={upd("budget")} data-testid={`enquiry-budget-${service}`} className="form-input" placeholder="e.g. $5k – $15k AUD" />
        </Field>
        <Field label="Number of students / participants (optional)" className="md:col-span-2">
          <input value={form.participant_count} onChange={upd("participant_count")} data-testid={`enquiry-participants-${service}`} className="form-input" placeholder="e.g. ~120 Yr 7–8 students" />
        </Field>
        <Field label="Describe your requirements" required className="md:col-span-2">
          <textarea required rows={5} value={form.requirements} onChange={upd("requirements")} data-testid={`enquiry-requirements-${service}`} className="form-input resize-y" placeholder="What are you trying to achieve? Where are you now? Any constraints or context we should know?" />
        </Field>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid={`enquiry-submit-${service}`}
        style={{ backgroundColor: accent }}
        className="mt-7 inline-flex items-center gap-2 text-white border-2 border-[#1A1A1A] rounded-full px-6 py-3.5 font-bold uppercase tracking-wide nb-shadow nb-press disabled:opacity-70"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} strokeWidth={2.5} />}
        {submitting ? "Sending…" : ctaLabel}
      </button>
      <p className="mt-3 text-xs text-[#4A4A4A]">
        We’ll reply to the email you provide within 1–2 business days.
      </p>

      <style>{`
        .form-input {
          width: 100%;
          background: #fff;
          border: 2px solid #1A1A1A;
          border-radius: 14px;
          padding: 12px 14px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          color: #1A1A1A;
          outline: none;
          transition: box-shadow 150ms ease, transform 150ms ease;
        }
        .form-input::placeholder { color: #999; font-weight: 500; }
        .form-input:focus {
          box-shadow: 4px 4px 0 0 #1A1A1A;
          transform: translate(-1px, -1px);
        }
      `}</style>
    </form>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[11px] uppercase tracking-widest font-bold text-[#1A1A1A]">
        {label}
        {required && <span className="text-[#FF007F]"> *</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
